/**
 * AudioIndicator - Audio playback status & controls
 *
 * Two variants:
 * - "phone": Inline element for header integration (no positioning)
 * - "tv": Floating bottom-right overlay with ARIA toolbar for remote control
 *
 * ARIA: role="toolbar" with aria-label for screen readers & TV remote navigation.
 * All interactive elements have explicit aria-labels and are keyboard-focusable.
 */

// React 19 removed the global JSX namespace; it now ships from the react package.
import type { JSX } from "react"
import { useTranslation } from "react-i18next"
import { useAudioStore } from "@/stores/audioStore"
import { usePlayerStore } from "@/stores/playerStore"
import { useGameStore } from "@/stores/gameStore"
import { audioService } from "@/services/audio"
import { emitEvent } from "@/services/socket"
import { PixelIcon } from "@/components/low/PixelIcon"

interface AudioIndicatorProps {
  variant: "tv" | "phone"
}

export function AudioIndicator({
  variant,
}: AudioIndicatorProps): JSX.Element | null {
  const { t } = useTranslation()
  const status = useAudioStore((state) => state.status)
  const isMuted = useAudioStore((state) => state.isMuted)

  if (status === "idle") {
    return null
  }

  // --- Handlers ---
  const handleMute = () => {
    audioService.mute()
    useAudioStore.getState().setMuted(true)
  }

  const handleUnmute = () => {
    audioService.unmute()
    useAudioStore.getState().setMuted(false)
  }

  const handleSkip = () => {
    audioService.skip()
    const store = useAudioStore.getState()
    const currentStreamId = store.streamId
    const displayPlayerId = useGameStore.getState().displayPlayerId
    const playerId = usePlayerStore.getState().playerId
    if (currentStreamId) {
      store.markFinished(currentStreamId)
    }

    // Emit guard: only display player emits (null-check critical — when null, all emit)
    if (displayPlayerId !== null && playerId !== displayPlayerId) {
      store.setSkipped()
      return
    }

    if (currentStreamId) {
      emitEvent('tts:playback:finished', { stream_id: currentStreamId })
    }
    audioService.markPlaybackFinished()
    store.setSkipped()
  }

  const handleReplay = () => {
    audioService.replay()
    useAudioStore.getState().setPlaying()
  }

  const isTv = variant === "tv"

  // --- Phone variant: compact inline element (no fixed/absolute positioning) ---
  if (!isTv) {
    return (
      <div
      role="toolbar"
      aria-label={t("audio.player")}
      className="flex items-center gap-1.5"
        data-testid="audio-indicator"
        data-audio-status={status}
      >
        {(status === "loading" || status === "resumed") && (
          <PixelIcon
            className="text-lg text-muted-foreground animate-pixel-blink"
            label={t("audio.loading")}
          >
            ⏳
          </PixelIcon>
        )}

        {(status === "playing" || status === "paused") && (
          <>
            <button
              onClick={isMuted ? handleUnmute : handleMute}
              className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
              aria-label={isMuted ? t("audio.unmute") : t("audio.mute")}
              data-testid="audio-mute-button"
            >
              {isMuted ? (
                <PixelIcon className="text-base">🔇</PixelIcon>
              ) : (
                <PixelIcon className="text-base">🔊</PixelIcon>
              )}
            </button>

            {status === "playing" && (
              <button
                onClick={() => audioService.pause()}
                className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
                aria-label={t("audio.pause")}
                data-testid="audio-pause-button"
              >
                <PixelIcon className="text-base">⏸️</PixelIcon>
              </button>
            )}

            {status === "paused" && (
              <button
                onClick={() => audioService.resume()}
                className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
                aria-label={t("audio.resume")}
                data-testid="audio-resume-button"
              >
                <PixelIcon className="text-base">▶️</PixelIcon>
              </button>
            )}

            <button
              onClick={handleSkip}
              className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
              aria-label={t("audio.skip")}
              data-testid="audio-skip-button"
            >
              <PixelIcon className="text-base">⏭️</PixelIcon>
            </button>
          </>
        )}

        {status === "skipped" && (
          <button
            onClick={handleReplay}
            className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
            aria-label={t("audio.replay")}
            data-testid="audio-replay-button"
          >
            <PixelIcon className="text-base">🔁</PixelIcon>
          </button>
        )}

        {status === "error" && (
          <>
            <PixelIcon className="text-lg" label={t("audio.error")}>
            ⚠️
          </PixelIcon>
          <button
            onClick={handleSkip}
            className="p-2.5 min-w-touch min-h-touch flex items-center justify-center hover:bg-accent rounded-lg transition-colors"
            aria-label={t("audio.skip")}
            data-testid="audio-skip-button"
            >
              <PixelIcon className="text-base">⏭️</PixelIcon>
            </button>
          </>
        )}
      </div>
    )
  }

  // --- TV variant: floating bottom-right, larger for 10-foot UI ---
  return (
    <div
      role="toolbar"
      aria-label={t("audio.player")}
      className="fixed bottom-6 right-6 z-40 bg-card/80 backdrop-blur-md border border-border rounded-xl px-5 py-3 flex items-center gap-4 shadow-lg"
      data-testid="audio-indicator"
      data-audio-status={status}
    >
      {(status === "loading" || status === "resumed") && (
        <>
          <PixelIcon className="text-xl animate-pixel-blink">⏳</PixelIcon>
          <span className="text-sm text-muted-foreground">
            {t("audio.loading")}
          </span>
        </>
      )}

      {(status === "playing" || status === "paused") && (
        <>
          <button
            onClick={isMuted ? handleUnmute : handleMute}
            className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label={isMuted ? t("audio.unmute") : t("audio.mute")}
            data-testid="audio-mute-button"
            tabIndex={0}
          >
            {isMuted ? (
              <PixelIcon className="text-lg">🔇</PixelIcon>
            ) : (
              <PixelIcon className="text-lg animate-pulse">🔊</PixelIcon>
            )}
          </button>

          {status === "playing" && (
            <>
              <span className="text-sm text-foreground/70">{t('audio.playing')}</span>
              <button
                onClick={() => audioService.pause()}
                className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                aria-label={t("audio.pause")}
                data-testid="audio-pause-button"
                tabIndex={0}
              >
                <PixelIcon className="text-lg">⏸️</PixelIcon>
              </button>
            </>
          )}

          {status === "paused" && (
            <>
              <span className="text-sm text-foreground/70">{t('audio.paused')}</span>
              <button
                onClick={() => audioService.resume()}
                className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                aria-label={t("audio.resume")}
                data-testid="audio-resume-button"
                tabIndex={0}
              >
                <PixelIcon className="text-lg">▶️</PixelIcon>
              </button>
            </>
          )}

          <button
            onClick={handleSkip}
            className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label={t("audio.skip")}
            data-testid="audio-skip-button"
            tabIndex={0}
          >
            <PixelIcon className="text-lg">⏭️</PixelIcon>
          </button>
        </>
      )}

      {status === "skipped" && (
        <>
          <span className="text-sm text-foreground/70">{t('audio.replay')}</span>
          <button
            onClick={handleReplay}
            className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label={t("audio.replay")}
            data-testid="audio-replay-button"
            tabIndex={0}
          >
            <PixelIcon className="text-lg">🔁</PixelIcon>
          </button>
        </>
      )}

      {status === "error" && (
        <>
          <PixelIcon className="text-xl" label={t("audio.error")}>
          ⚠️
        </PixelIcon>
        <span className="text-sm text-destructive">{t("audio.error")}</span>
        <button
          onClick={handleSkip}
          className="p-2 hover:bg-accent rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
          aria-label={t("audio.skip")}
            data-testid="audio-skip-button"
            tabIndex={0}
          >
            <PixelIcon className="text-lg">⏭️</PixelIcon>
          </button>
        </>
      )}
    </div>
  )
}
